import React, { useState, useEffect, useRef } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { AIRAMark } from '@/components/brand/Marks';

export default function AsyncNarrative({ sr, submissionId }) {
  const api = useApi();
  const [narrative, setNarrative] = useState(sr?.ai_narrative || null);
  const [status, setStatus] = useState(sr?.ai_narrative ? 'ready' : (sr?.narrative_status || null));
  const tries = useRef(0);

  useEffect(() => {
    setNarrative(sr?.ai_narrative || null);
    setStatus(sr?.ai_narrative ? 'ready' : (sr?.narrative_status || null));
    tries.current = 0;
  }, [sr, submissionId]);

  useEffect(() => {
    if (status !== 'pending' || !submissionId) return undefined;
    const timer = setInterval(async () => {
      tries.current += 1;
      if (tries.current > 20) { clearInterval(timer); setStatus('failed'); return; }
      try {
        const { data } = await api.get(`/submissions/${submissionId}/narrative`);
        if (data.status === 'ready' && data.ai_narrative) {
          setNarrative(data.ai_narrative); setStatus('ready'); clearInterval(timer);
        } else if (data.status === 'failed') { setStatus('failed'); clearInterval(timer); }
      } catch { /* keep polling */ }
    }, 2500);
    return () => clearInterval(timer);
  }, [status, submissionId, api]);

  if (status === 'pending') {
    return (
      <Card className="rounded-sm border-l-4 bg-white p-5" style={{ borderLeftColor: '#00B4D8' }} data-testid="ai-narrative-pending">
        <div className="flex items-center gap-2 mb-3">
          <AIRAMark size="text-sm" />
          <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#00B4D8' }}>AI Risk Assessment</span>
          <span className="flex items-center gap-1.5 text-[11px]" style={{ color: '#5C6B7A' }}>
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#00B4D8' }} />
            ARIA is writing the narrative — your decision above is final and does not depend on it
          </span>
        </div>
        <div className="space-y-2">
          {[92, 100, 78].map((w, i) => (
            <div key={i} className="h-3 rounded animate-pulse" style={{ width: `${w}%`, background: '#EDF1F5' }} />
          ))}
        </div>
      </Card>
    );
  }
  if (!narrative) return null;
  return (
    <Card className="rounded-sm border-l-4 bg-white p-5" style={{ borderLeftColor: '#00B4D8' }} data-testid="ai-narrative">
      <div className="flex items-center gap-2 mb-3">
        <AIRAMark size="text-sm" />
        <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#00B4D8' }}>AI Risk Assessment</span>
      </div>
      <div className="text-sm leading-relaxed whitespace-pre-line" style={{ color: '#0D1B2A' }}>{narrative}</div>
    </Card>
  );
}
